import { ArrowRightIcon } from '@heroicons/react/16/solid';
import { PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import { Button, Spinner } from 'flowbite-react';
import { useCallback, useEffect, useState } from 'react';
import { useTypedFetcher } from 'remix-typedjson';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { useVoiceSlug } from '#speaker-shop/hooks/useVoiceSlug';
import { type action as validateCouponAction } from '../routes/api_+/validate-coupon';
import { Checkbox } from './Checkbox';

// type CouponInfo = { type: 'percentage'; discount: number } | { type: 'fixed-amount'; value: number; currency: string };
export interface CouponInfo {
    type: 'percentage';
    discount: number;
    code: string;
}
interface CouponEntryProps {
    onCouponApplied?: (couponInfo: CouponInfo | null) => void;
    /** Applied to the "I have a coupon" checkbox — lets callers scale it down. */
    checkboxClassName?: string;
    /** Renders a plain browser checkbox instead of the icon-based one (used by the v2 core shop). */
    nativeCheckbox?: boolean;
}

export function CouponEntry({ onCouponApplied, checkboxClassName, nativeCheckbox }: CouponEntryProps) {
    const [ hasCoupon, setHasCoupon ] = useState(false);
    const [ coupon, setCoupon ] = useState('');
    const [ appliedCoupon, setAppliedCoupon ] = useState<CouponInfo | null>(null);
    const [ hasError, setHasError ] = useState(false);
    const { t, rawT } = useEditableTranslation('checkout');
    const fetcher = useTypedFetcher<typeof validateCouponAction>();
    const disabled = fetcher.state !== 'idle' || !coupon.trim();
    const voiceSlug = useVoiceSlug();

    useEffect(() => {
        if (fetcher.data) {
            if (fetcher.data.valid) {
                const couponData =
                    //  fetcher.data.type === 'percentage'                    ?
                    { type: 'percentage' as const, discount: fetcher.data.value, code: fetcher.data.code };
                // : { type: 'fixed-amount' as const, value: fetcher.data.value, currency: fetcher.data.currency };
                setAppliedCoupon(couponData);
                setHasError(false);
                onCouponApplied?.(couponData);
            } else {
                setHasError(true);
                setAppliedCoupon(null);
                onCouponApplied?.(null);
            }
        }
    }, [ fetcher.data, onCouponApplied ]);

    const onApplyCoupon = () => {
        setHasError(false);
        fetcher.submit({ coupon, ...(voiceSlug ? { voiceSlug } : {}) }, {
            method: 'POST',
            action: '/api/validate-coupon'
        });
    };

    const onDeleteCoupon = useCallback(() => {
        setAppliedCoupon(null);
        setCoupon('');
        setHasError(false);
        onCouponApplied?.(null);
    }, [ onCouponApplied, setAppliedCoupon, setCoupon, setHasError ]);

    useEffect(() => {
        if (!hasCoupon) {
            onDeleteCoupon();
        }
    }, [ hasCoupon, onDeleteCoupon ]);

    return (
        <>
            {nativeCheckbox
                ? (
                    <label className={clsx('flex items-center gap-2', checkboxClassName)}>
                        <input
                            type='checkbox'
                            name='hasCoupon'
                            className='accent-cvyellow'
                            checked={hasCoupon}
                            onChange={e => setHasCoupon(e.target.checked)}
                        />
                        <span>{t('has-coupon')}</span>
                    </label>
                )
                : (
                    <Checkbox
                        label={t('has-coupon')}
                        isChecked={hasCoupon}
                        onChange={setHasCoupon}
                        name='hasCoupon'
                        className={checkboxClassName}
                    />
                )}
            {hasCoupon && !appliedCoupon && (
                <div className='mt-2 flex flex-col gap-2'>
                    <div className='flex flex-row'>
                        <input
                            placeholder={rawT('coupon')}
                            value={coupon}
                            onChange={e => {
                                setCoupon(e.target.value);
                                setHasError(false);
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (!disabled) {
                                        onApplyCoupon();
                                    }
                                }
                            }}
                            onBlur={e => {
                                if (!disabled) {
                                    onApplyCoupon();
                                }
                            }}
                            type='text'
                            className={clsx(
                                'rounded w-full p-2',
                                hasError
                                    ? 'border-2 ring-2 ring-white border-red-600 text-red-600 bg-red-200'
                                    : 'text-black bg-white'
                            )}
                        >
                        </input>
                        <Button
                            className={clsx('ml-2 p-2', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}
                            type='button'
                            color='white'
                            onClick={onApplyCoupon}
                            disabled={disabled}
                        >
                            {fetcher.state !== 'idle' && <Spinner size='md' className='mr-2' />}
                            {fetcher.state === 'idle' && <PaperAirplaneIcon className='w-6 h-6' />}
                        </Button>
                    </div>
                </div>
            )}
            {hasCoupon && appliedCoupon && (
                <>
                    <input type='hidden' name='couponCode' value={appliedCoupon.code} />
                    <div className='flex flex-row items-center gap-2 p-2 bg-green-50 rounded border border-green-200 text-green-800'>
                        <div className='flex-1 flex flex-col'>
                            <span>{t('coupon')}: {appliedCoupon.code}</span>
                            <div className='flex items-center gap-1'>
                                <ArrowRightIcon className='w-5 h-5' />
                                {t('percentage-coupon', { discount: appliedCoupon.discount })}
                            </div>
                        </div>
                        <Button className='p-1' type='button' color='white' onClick={onDeleteCoupon}>
                            <XMarkIcon className='w-5 h-5' />
                        </Button>
                    </div>
                </>
            )}
        </>
    );
}
